import { getBrandingByName, getBrandingByToken } from './branding-client';
import {
  buildBrandingSetFromResponse,
  getBrandingFromWindow,
  getBrandingSetNameFromUrl,
  getContextPath,
  getCustomerTokenFromUrl,
  getDefaultBranding,
} from './helpers';
import type { BrandingClientSet, BrandingClientSetResponse } from './types';

export type ResolveBrandingFromUrlOptions = {
  defaultContextPath: string;
  /**
   * Context path for buildBrandingSetFromResponse.
   * Defaults to getContextPath(pageUrl) ?? defaultContextPath.
   */
  buildContextPath?: string;
};

export type ResolvedBrandingFromUrl = {
  brandingSet: BrandingClientSet;
  /** When true, callers should write the set to window.mimecast.branding. */
  shouldPersistToWindow: boolean;
  /** Context path to store as originalContextPath when persisting. */
  contextPath: string;
};

/**
 * Resolves branding for a page URL (token, subdomain name, window preload, or default).
 * Token is tried first when present; if that lookup fails or returns no items,
 * branding falls back to the URL prefix (subdomain name). Matches legacy BrandingFilter.
 * Pure of DOM side effects - callers apply CSS/favicon and optionally persist to window.
 */
export async function resolveBrandingSetFromUrl(
  pageUrl: string,
  options: ResolveBrandingFromUrlOptions
): Promise<ResolvedBrandingFromUrl> {
  const { defaultContextPath, buildContextPath } = options;
  const token = getCustomerTokenFromUrl(pageUrl);
  const brandingName = getBrandingSetNameFromUrl(pageUrl);
  const contextPath = getContextPath(pageUrl) ?? defaultContextPath;
  const responseContextPath = buildContextPath ?? contextPath;

  if (!token && !brandingName) {
    const preloaded = getBrandingFromWindow(globalThis);
    if (preloaded) {
      return {
        brandingSet: preloaded,
        shouldPersistToWindow: false,
        contextPath,
      };
    }
  }

  // Token first; empty/failed token allows URL prefix fallback (UIP-21719)
  if (token) {
    const brandingSet = await fetchBrandingSet(responseContextPath, () =>
      getBrandingByToken(token)
    );
    if (brandingSet) {
      return {
        brandingSet,
        shouldPersistToWindow: true,
        contextPath,
      };
    }

    const preloaded = getBrandingFromWindow(globalThis);
    if (preloaded) {
      return {
        brandingSet: preloaded,
        shouldPersistToWindow: false,
        contextPath,
      };
    }
  }

  if (brandingName) {
    const brandingSet = await fetchBrandingSet(responseContextPath, () =>
      getBrandingByName(brandingName)
    );
    if (brandingSet) {
      return {
        brandingSet,
        shouldPersistToWindow: true,
        contextPath,
      };
    }

    const preloaded = getBrandingFromWindow(globalThis);
    if (preloaded) {
      return {
        brandingSet: preloaded,
        shouldPersistToWindow: false,
        contextPath,
      };
    }
  }

  return {
    brandingSet: getDefaultBranding(contextPath),
    shouldPersistToWindow: true,
    contextPath,
  };
}

async function fetchBrandingSet(
  contextPath: string,
  fetchBranding: () => Promise<BrandingClientSetResponse>
): Promise<BrandingClientSet | null> {
  try {
    const response = await fetchBranding();
    if (!hasBrandingResponseItems(response)) {
      return null;
    }
    return await buildBrandingSetFromResponse(contextPath, response);
  } catch {
    return null;
  }
}

function hasBrandingResponseItems(
  response: BrandingClientSetResponse
): boolean {
  const data = response?.data;
  if (!data || !Array.isArray(data) || data.length < 1) {
    return false;
  }
  return Boolean(data[0].items?.length);
}
